import { AnyAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit'

import {
    getComments,
    getEphemeralList,
    getEphemeralPost,
    postLikePost,
    postLikePostCancel,
    updatePost,
    uploadPhoto
} from '../../api/ephemeral'
import { LikeResponse, SubmitEphemeral, UpdatePost, UploadPhotoPayload } from './ephemeralTypings'
import { currentErrorInfo } from '../../api/axios'

const initialState: any = {
    list: [],
    isLoadingList: false,
    post: null,
    isLoadingPost: false,
    error: null,
    hasMorePosts: false,
    additionalPosts: [],
    postLoadedCounter: 0,
    postUploading: false
}

export const fetchEphemeralPost = createAsyncThunk('fetchEphemeralPost', async (id: string | number) => {
    return getEphemeralPost(id)
})

export const fetchComments = createAsyncThunk('fetchComments', async (params: { id: number; offset: number }) => {
    return getComments(params.id, params.offset)
})

export const fetchLikeList = createAsyncThunk('fetchComments', async (params: { id: number; offset: number }) => {
    return getComments(params.id, params.offset)
})

export const fetchEphemeralList = createAsyncThunk<any, { limit?: number; offset?: number }>(
    'fetchEphemeralList',
    async (params) => {
        return getEphemeralList(params)
    }
)

export const photoUpload = createAsyncThunk<UploadPhotoPayload, SubmitEphemeral>('photo/upload', (params) =>
    uploadPhoto(params)
)

export const likePost = createAsyncThunk<LikeResponse, { id: number; parent_id: number }>('post/like', (params) =>
    postLikePost({ id: params.id })
)

export const likePostCancel = createAsyncThunk<LikeResponse, { id: number; parent_id: number }>(
    'post/like/cancel',
    (params) => postLikePostCancel({ id: params.id })
)

//@ts-ignore
function addCommentsWithMetadata(obj, targetId, newComments, hasMoreComments) {
    const newObj = { ...obj }

    if (newObj.id === targetId) {
        const updatedComments = [...(newObj.comments || []), ...newComments]

        return {
            ...newObj,
            comments: updatedComments,
            currentLength: updatedComments.length,
            hasMoreComments: hasMoreComments,
            commentsLoading: false
        }
    }

    if (newObj.comments) {
        let commentsChanged = false
        //@ts-ignore
        const updatedComments = newObj.comments.map((comment) => {
            const updated = addCommentsWithMetadata(comment, targetId, newComments, hasMoreComments)
            if (updated !== comment) {
                commentsChanged = true
                return updated
            }
            return comment
        })

        if (commentsChanged) {
            return { ...newObj, comments: updatedComments }
        }
    }

    return newObj
}

//@ts-ignore
function switchCommentsOption(obj, targetId, key, isLoading) {
    const newObj = { ...obj }

    if (newObj.id === targetId) {
        return {
            ...newObj,
            [`${key}`]: isLoading
        }
    }

    if (newObj.comments && Array.isArray(newObj.comments)) {
        let commentsChanged = false
        //@ts-ignore
        const updatedComments = newObj.comments.map((comment) => {
            const updated = switchCommentsOption(comment, targetId, key, isLoading)
            if (updated !== comment) {
                commentsChanged = true
                return updated
            }
            return comment
        })

        if (commentsChanged) {
            return { ...newObj, comments: updatedComments }
        }
    }

    return newObj
}

export const updateEphemeralPost = createAsyncThunk('ephemeral/updatePost', async (params: UpdatePost) => {
    return updatePost(params)
})

const ephemeralSlice = createSlice({
    name: 'ephemeral',
    initialState,
    reducers: {
        photoUploadError(state, action?: AnyAction) {
            console.log(action)
            state.error = action.payload ? action.payload : 'You can load only one photo'
        },
        photoUploadErrorClean(state) {
            state.error = null
        },
        clearPost(state) {
            state.post = null
        },
        clearEphemeralList(state) {
            state.list = []
        },
        resetPostLoadedCounter(state) {
            state.postLoadedCounter = 0
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(updateEphemeralPost.pending, (state) => {
                state.isLoadingPost = true
            })
            .addCase(updateEphemeralPost.rejected, (state) => {
                state.isLoadingPost = false
            })
            .addCase(updateEphemeralPost.fulfilled, (state, action: AnyAction) => {
                state.isLoadingPost = false

                const updatedPost = action.payload.contribution
                //@ts-ignore
                state.list = state.list.map((post) => (post.id === updatedPost.id ? { ...post, ...updatedPost } : post))

                if (state.post && state.post.id && state.post.id === updatedPost.id) {
                    state.post = { ...state.post, ...updatedPost }
                }

                //@ts-ignore
                state.additionalPosts = state.additionalPosts.map((post) =>
                    post.id === updatedPost.id ? { ...post, ...updatedPost } : post
                )
            })

            .addCase(fetchEphemeralList.pending, (state) => {
                state.isLoadingList = true
            })
            .addCase(fetchEphemeralList.rejected, (state, action) => {
                if (action.meta.arg.offset > 0) {
                    state.isLoadingMoreList = false
                    state.hasMorePosts = false
                } else {
                    state.isLoadingList = false
                    state.hasMorePosts = false
                }
            })
            .addCase(fetchEphemeralList.fulfilled, (state, action: AnyAction) => {
                const newPosts = action.payload.list
                const hasMorePosts = newPosts.length === 11

                if (action.meta.arg.offset > 0) {
                    state.list = [...state.list, ...newPosts.slice(0, 10)]
                    state.isLoadingMoreList = false
                } else {
                    state.list = newPosts.slice(0, 10)
                    state.isLoadingList = false
                }

                state.hasMorePosts = hasMorePosts
            })
            .addCase(fetchEphemeralPost.pending, (state) => {
                state.isLoadingPost = true
            })
            .addCase(fetchEphemeralPost.rejected, (state) => {
                state.isLoadingPost = false
            })
            .addCase(fetchEphemeralPost.fulfilled, (state, action: AnyAction) => {
                state.isLoadingPost = false
                //@ts-ignore
                function trimLongCommentsImmutable(obj) {
                    if (!obj || typeof obj !== 'object') {
                        return obj
                    }
                    const newObj = { ...obj }
                    if (Array.isArray(newObj.comments)) {
                        let hasMoreComments = false
                        let trimmedComments = newObj.comments

                        if (trimmedComments.length > 10) {
                            trimmedComments = trimmedComments.slice(0, 10)
                            hasMoreComments = true
                        }
                        //@ts-ignore
                        newObj.comments = trimmedComments.map((comment) => trimLongCommentsImmutable(comment))
                        if (hasMoreComments) {
                            newObj.hasMoreComments = true
                            newObj.currentLength = 10
                        } else if ('hasMoreComments' in newObj) {
                            delete newObj.hasMoreComments
                        }
                    }

                    return newObj
                }

                state.post = trimLongCommentsImmutable(action.payload.contribution)
            })

            .addCase(fetchComments.pending, (state, action: AnyAction) => {
                state.post = switchCommentsOption(state.post, action.meta.arg.id, 'commentsLoading', true)
            })
            .addCase(fetchComments.rejected, (state, action: AnyAction) => {
                state.post = switchCommentsOption(state.post, action.meta.arg.id, 'commentsLoading', false)
            })
            .addCase(fetchComments.fulfilled, (state, action: AnyAction) => {
                const list = action.payload.list
                const hasMoreComments = list.length === 11
                state.post = addCommentsWithMetadata(
                    state.post,
                    action.meta.arg.id,
                    action.payload.list.slice(0, 10),
                    hasMoreComments
                )
                state.isLoadingPost = false
            })

            .addCase(photoUpload.pending, (state, action: AnyAction) => {
                state.postUploading = true
            })
            .addCase(photoUpload.rejected, (state, action: AnyAction) => {
                state.postUploading = false
                state.error = currentErrorInfo.message
                state.postLoadedCounter = state.postLoadedCounter + 1
            })
            .addCase(photoUpload.fulfilled, (state, action: AnyAction) => {
                state.postUploading = false
                state.additionalPosts = [action.payload.contribution, ...state.additionalPosts]
                state.postLoadedCounter = state.postLoadedCounter + 1
            })
    }
})
export const { photoUploadError, photoUploadErrorClean, clearPost, clearEphemeralList, resetPostLoadedCounter } =
    ephemeralSlice.actions
export default ephemeralSlice.reducer
